import React, { ReactNode, useState } from "react";
import { Box, useTheme } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { FC } from "../../../../types";
import {
  StyledDayButton,
  StyledDayCalendar,
  StyledSwitchButton,
  StyledWeekdayButton,
  StyledWeekdayCalendar,
} from "./DayCalendar.styled";

interface DayCalendarProps {
  value: number | null;
  isWeekday: boolean;
  isDaysOfWeekEnabled: boolean;
  extraFooterComponent?: ReactNode;
  onChange(value: number | null, isWeekday?: boolean): void;
}

export enum DayCalendarMode {
  DAY_OF_MONTH,
  DAY_OF_WEEK,
}

const DAYS_NUMBER = 31;

export const DayCalendar: FC<DayCalendarProps> = (props) => {
  const {
    value,
    isWeekday,
    isDaysOfWeekEnabled,
    extraFooterComponent,
    onChange,
  } = props;

  const [mode, setMode] = useState(
    isWeekday && isDaysOfWeekEnabled
      ? DayCalendarMode.DAY_OF_WEEK
      : DayCalendarMode.DAY_OF_MONTH,
  );

  const theme = useTheme();

  const switchMode = () => {
    onChange(null);
    setMode((prevMode) =>
      prevMode === DayCalendarMode.DAY_OF_MONTH
        ? DayCalendarMode.DAY_OF_WEEK
        : DayCalendarMode.DAY_OF_MONTH,
    );
  };

  const switchLabel =
    mode === DayCalendarMode.DAY_OF_MONTH
      ? "Switch to days of week"
      : "Switch to days number";
  const days = Array.from<Dayjs>({ length: DAYS_NUMBER }).map(
    (_, index) => index + 1,
  );
  const weekdays = dayjs.weekdaysShort();

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      {mode === DayCalendarMode.DAY_OF_WEEK ? (
        <StyledWeekdayCalendar>
          {weekdays.map((weekday, index) => (
            <StyledWeekdayButton
              key={weekday}
              buttonType="secondary"
              label={`${weekday}`}
              isSelected={value === index}
              onClick={() => onChange(index, true)}
            />
          ))}
        </StyledWeekdayCalendar>
      ) : (
        <StyledDayCalendar>
          {days.map((day) => (
            <StyledDayButton
              key={day}
              buttonType="secondary"
              label={`${day}`}
              isSelected={value === day}
              onClick={() => onChange(day, false)}
            />
          ))}
        </StyledDayCalendar>
      )}
      <Box
        display="flex"
        justifyContent="space-between"
        width="100%"
        padding={`${theme.wsc.spacing[2]} ${theme.wsc.spacing[2]} 0 ${theme.wsc.spacing[2]}`}
      >
        {isDaysOfWeekEnabled ? (
          <StyledSwitchButton
            label={switchLabel}
            buttonType="secondary"
            onClick={switchMode}
          />
        ) : (
          <div />
        )}
        {extraFooterComponent}
      </Box>
    </Box>
  );
};
